import { useMutation } from '@apollo/client'
import { Button, Cascader, Checkbox, Col, Form, InputNumber, message, Popconfirm, Row, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginModal } from '../../../../../context/loginModalContext'
import { showAuthError } from '../../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../../common/CommonStyles'
import { v4 as uuidv4 } from 'uuid'
import { useForceUpdate } from '../../../../../shared/hooks/useForceUpdate'
import CreateNewItemExtraButtons from '../../../../common/CreateNewItemExtraButtons'
import { useNavigate, useParams } from 'react-router-dom'
import NameField from '../../../../common/formFields/NameField'
import ADD_NEW_PATIENT_TAGS from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/patientTags/addNewPatientTags'
import EDIT_PATIENT_TAG from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/patientTags/editPatientTag'
import { DeleteOutlined } from '@ant-design/icons'
import DELETE_PATIENT_TAG from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/patientTags/deletePatientTag'
import SEARCH_PATIENT_TAGS from '@dentolize/clinic-mobile/src/shared/store/queries/actions/patientTags/searchPatientTags'
import UserColorPicker from '../../Users/UserColorPicker'
import SearchField from '../../../../common/searchFields/SearchField'
import tagIcons from '../../../../../shared/utils/tagIcons'
import { DATABASE_ICONS_URL } from '../../../../../variables'

const PatientTagForm = props => {
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const { patientTag } = useParams()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, setFieldsValue } = form

  const cascadeFilter = (inputValue, path) => path.some(option => option.label?.toLowerCase().includes(inputValue.toLowerCase()))

  useEffect(() => {
    if (formVisible) {
      resetFields()
      if (selectedItem.new) {
        setFieldsValue({
          color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
        })
      }
    }
  }, [formVisible])

  const [addNewItem, { loading }] = useMutation(ADD_NEW_PATIENT_TAGS)
  const [editItem, { loading: loadingEdit }] = useMutation(EDIT_PATIENT_TAG)
  const [deleteItem, { loading: loadingDelete }] = useMutation(DELETE_PATIENT_TAG, {
    update: handleUpdateAfterDelete
  })

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  function handleUpdateAfterDelete(store, deletedItem) {
    store.data.delete(`PatientTag:${deletedItem.data.deletePatientTag.id}`)
    props.handleAnimateRow && props.handleAnimateRow(null, true)
  }

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let item

          if (selectedItem.new) {
            item = await addNewItem({
              variables: {
                ...values,
                icon: values.icon?.[1] || null,
                nextTags: values.nextTags?.map(s => s.key) || [],
                generatedID
              }
            })

            if (key === 'view') {
              navigate(`/settings/types/tags/${generatedID}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
              setFieldsValue({
                color: `#${Math.floor(Math.random() * 16777215).toString(16)}`
              })
            } else {
              handleClose(item.data.addNewPatientTags)
            }
          } else {
            item = await editItem({
              variables: {
                ...values,
                icon: values.icon?.[1] || null,
                nextTags: values.nextTags?.map(s => s.key).filter(s => s !== selectedItem.id) || [],
                patientTag: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (item.data.addNewPatientTags && item.data.addNewPatientTags.id) ||
                (item.data.editPatientTag && item.data.editPatientTag.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  const handleDelete = async () => {
    try {
      await deleteItem({
        variables: {
          patientTag: selectedItem.id
        }
      })

      message.success(t('common.deletedSuccess'))
      handleClose()

      if (patientTag) {
        navigate(-1)
      }
    } catch (e) {
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  return (
    <StyledDrawer
      title={!selectedItem.new ? t('common.edit') : t('patients.newItem')}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new ? (
            <Popconfirm
              title={t('common.sureDelete')}
              cancelText={t('common.no')}
              okText={t('common.yes')}
              onConfirm={handleDelete}
              disabled={loading || loadingEdit}
            >
              <Button loading={loadingDelete} disabled={loading || loadingEdit} type="primary" danger icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button loading={loading || loadingEdit} htmlType="submit" type="primary" form={`${generatedID}patientTagForm`}>
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}patientTagForm`}
          onFinish={handleSubmit}
          initialValues={
            selectedItem
              ? {
                  ...selectedItem,
                  icon: selectedItem.icon
                    ? [
                        `${selectedItem.icon
                          .split('/')
                          .pop()
                          .split('_')[0]
                          .charAt(0)
                          .toUpperCase()}${selectedItem.icon
                          .split('/')
                          .pop()
                          .split('_')[0]
                          .substring(1)}`,
                        selectedItem.icon
                      ]
                    : undefined,
                  nextTags:
                    selectedItem.nextTags?.map(s => ({
                      label: s.name,
                      key: s.id
                    })) ?? []
                }
              : selectedItem
          }
        >
          <Row type="flex" gutter={16}>
            <Col span={16}>
              <NameField required min={1} max={100} autoFocus autoComplete="" />
            </Col>
            <Col span={8}>
              <Form.Item name="color" label={t('settings.groups.color')}>
                <UserColorPicker />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="order" label={t('app.order')}>
                <InputNumber min={0} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="icon" label={t('patients.image')}>
                <Cascader
                  size="large"
                  options={tagIcons.map(group => ({
                    label: group.name,
                    value: group.name,
                    children: group.icons.map(p => ({
                      label: (
                        <span className="tag-icon">
                          <img loading="lazy" src={`${DATABASE_ICONS_URL}/tag-icons/${p}`} alt={p} />
                        </span>
                      ),
                      value: `${DATABASE_ICONS_URL}/tag-icons/${p}`
                    }))
                  }))}
                  displayRender={e => e[e.length - 1]}
                  expandTrigger="hover"
                  showSearch={{ filter: cascadeFilter }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <SearchField
                GET_QUERY={SEARCH_PATIENT_TAGS}
                label={t('app.nextTags')}
                fieldName="nextTags"
                dataName="searchPatientTags"
                multiple
              />
            </Col>
            <Col span={12}>
              <Form.Item help={t('app.forQrCodeDesc')} name="forQrCode" valuePropName="checked">
                <Checkbox>Qr Code</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item help={t('app.forWhatsAppDesc')} name="forWhatsApp" valuePropName="checked">
                <Checkbox>WhatsApp</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

PatientTagForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default PatientTagForm
